// components/ProtectedRoute.tsx
import React, { useEffect, useRef, useSyncExternalStore } from 'react';
import Spinner from '@/components/ui/spinner';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import type { RootState, AppDispatch } from '../store/store';
import {
  getUserInfo,
  hasCheckedSessionIdentity,
  reconcileSessionIdentity,
  signOut,
  subscribeToSessionIdentityCheck,
} from '../services/auth';
import { fetchExamMetadata, fetchExamStats } from '../services/exams';
import { logout } from '../slices/auth/authSlice';
import { userLoading, userFailed } from '../slices/user/userSlice';
import { examsLoading, examsFailed } from '../slices/exams/examsSlice';
import { isLegacyExchangePending } from '../services/legacyExchange';
import { addSentryBreadcrumb } from '../utils/sentry';
import { syncUserTimezone } from '../utils/syncUserTimezone';

interface ProtectedRouteProps {
  children: React.ReactNode;
  authPath?: string;
}

const isAllowedPublicPath = (pathname: string) =>
  pathname === '/demo' ||
  pathname.startsWith('/demo/') ||
  pathname === '/help' ||
  pathname.startsWith('/help/') ||
  pathname === '/games' ||
  pathname === '/games/daily-question' ||
  pathname === '/games/doctordle' ||
  pathname === '/games/doctordle/results' ||
  pathname === '/games/doctordle/archive' ||
  pathname.startsWith('/study/webinar');

/**
 * Routes that render for a signed-in student before onboarding is finished.
 * `SessionGates` treats these as not-yet-a-session too, so a policy prompt or
 * the welcome never lands on the onboarding flow itself.
 */
export const isAllowedPreOnboardingPath = (pathname: string) =>
  pathname === '/onboarding' ||
  // The help docs are public; a student mid-onboarding may need them too.
  pathname === '/help' ||
  pathname.startsWith('/help/') ||
  pathname === '/faculty' ||
  pathname.startsWith('/faculty/') ||
  pathname === '/games' ||
  pathname.startsWith('/games/') ||
  pathname.startsWith('/study/webinar') ||
  // A practice question opened from an email (ORA-948), which a student who
  // signed up but never finished onboarding is exactly who gets.
  pathname.startsWith('/study/question/');

/**
 * Account > Security. An admin without two-factor authentication is sent
 * here (`?setup2fa=1`) and refused the exam APIs (and so onboarding) until
 * they enrol, so it renders before onboarding and without exam metadata.
 */
const isSecuritySettingsLocation = (pathname: string, search: string) => {
  if (pathname !== '/account') return false;
  const params = new URLSearchParams(search);
  return (params.get('sub') ?? params.get('tab')) === 'security';
};

const ProtectedRoute = ({
  children,
  authPath = '/auth',
}: ProtectedRouteProps): JSX.Element | null => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, status: authStatus } = useSelector(
    (state: RootState) => state.auth
  );

  const { profile, status: userStatus } = useSelector(
    (state: RootState) => state.user
  );

  const { status: examsStatus, ids: examIds } = useSelector(
    (state: RootState) => state.exams
  );

  // The session is an httpOnly cookie (ORA-987): only an in-flight sign-in
  // is "potentially authenticating"; the persisted flag covers the rest.
  const isPotentiallyAuthenticating = authStatus === 'loading';
  const hasExamMetadata = examIds.length > 0;

  const isSecuritySettings = isSecuritySettingsLocation(
    location.pathname,
    location.search
  );

  const isInitialExamMetadataLoading =
    examsStatus === 'loading' && !hasExamMetadata;

  const isDataLoading =
    userStatus === 'loading' || isInitialExamMetadataLoading;

  // The persisted state is a cached copy: ask the server whose session this
  // is before any protected UI renders (once per page load).
  const isIdentityChecked = useSyncExternalStore(
    subscribeToSessionIdentityCheck,
    hasCheckedSessionIdentity
  );

  useEffect(() => {
    if (isAuthenticated && !isIdentityChecked) {
      void reconcileSessionIdentity();
    }
  }, [isAuthenticated, isIdentityChecked]);

  // A public page already on screen (the webinar live page, whose modal
  // signs the student up) keeps rendering while the identity check and the
  // profile load run in the background: swapping it for the spinner would
  // unmount it mid-setup. Such pages render without a profile by design.
  const isPublicPath = isAllowedPublicPath(location.pathname);
  const hasRenderedPublicChildren = useRef(false);
  if (!isPublicPath) hasRenderedPublicChildren.current = false;
  const keepsPublicChildren = isPublicPath && hasRenderedPublicChildren.current;

  const renderChildren = () => {
    if (isPublicPath) hasRenderedPublicChildren.current = true;
    return <>{children}</>;
  };

  const needsDataFetch =
    isAuthenticated &&
    isIdentityChecked &&
    !profile &&
    !isDataLoading &&
    userStatus !== 'failed';

  useEffect(() => {
    if (needsDataFetch) {
      const fetchData = async () => {
        dispatch(userLoading());
        dispatch(examsLoading());
        try {
          // Reuses a profile another boot caller fetched moments ago.
          const userInfo = await getUserInfo({ reuseRecent: true }); // Dispatches userReceived internally

          // The exam metadata is not needed to render every page, and an
          // admin without two-factor authentication is refused it (403
          // TWO_FACTOR_SETUP_REQUIRED) while the profile still loads: a
          // failure here must not keep the route on the spinner or sign out.
          try {
            await fetchExamMetadata(); // Dispatches examsReceived internally
          } catch (error) {
            console.error('ProtectedRoute: Exam metadata fetch failed.', error);

            dispatch(
              examsFailed(
                error instanceof Error
                  ? error.message
                  : 'Failed to fetch exam metadata'
              )
            );

            endSessionOnAuthFailure(error);
          }

          if (userInfo?.isOnboarded) {
            fetchExamStats().catch((error) => {
              console.error('ProtectedRoute: Exam stats fetch failed.', error);
            });
          }

          // Handle explicit anonymous user case trying to access protected areas
          // Note: /demo is handled by the redirect logic below if !isAuthenticated
          if (userInfo?.isAnon) {
            const isAllowedAnonPath =
              location.pathname === '/demo' ||
              location.pathname.startsWith('/demo/');

            if (!isAllowedAnonPath) {
              console.log(
                'ProtectedRoute: Anon user on protected path, logging out.'
              );

              // Revokes the demo session cookie too.
              void signOut();
            }
            // If on allowed path, continue rendering (auth state might still be true briefly)
          } else if (
            !userInfo &&
            isAuthenticated &&
            // Legacy tokens this boot could not trade yet: the missing
            // profile is the missing cookie; keep the tokens for the retry.
            !isLegacyExchangePending()
          ) {
            // If getUserInfo failed silently but we are still authenticated somehow
            console.error(
              'ProtectedRoute: getUserInfo returned no data while authenticated, logging out.'
            );

            dispatch(logout());
          }
        } catch (error) {
          console.error('ProtectedRoute: Data fetch failed.', error);

          const errMessage =
            error instanceof Error ? error.message : 'Failed to fetch data';

          // Record failures for UI/debug
          dispatch(userFailed(errMessage));
          dispatch(examsFailed(errMessage));
          endSessionOnAuthFailure(error);
        }
      };

      /**
       * Signs out only when the session is gone (401). A 403 is an answer
       * from a live session (a plan refusal; an admin without 2FA, whom the
       * axios client sends to the Security tab), and a 401 while legacy
       * tokens wait for their exchange is the missing cookie.
       */
      const endSessionOnAuthFailure = (error: unknown) => {
        if (!axios.isAxiosError(error)) return;
        const status = error.response?.status;
        if (status !== 401 || isLegacyExchangePending()) return;

        console.warn(
          'ProtectedRoute: Auth failure detected during data fetch. Logging out.'
        );

        addSentryBreadcrumb({
          category: 'auth-guard',
          level: 'warning',
          message: 'ProtectedRoute logout due to auth failure',
          data: {
            path: location.pathname,
            status,
          },
        });

        dispatch(logout());
      };

      fetchData();
    }
  }, [dispatch, needsDataFetch, isAuthenticated, location.pathname]);

  // Keyed on the profile itself (not the fetch above) because rehydrated
  // boots skip the fetch entirely: redux-persist restores the user slice, so
  // returning users would otherwise never reach the sync. Internal guards
  // (anon, impersonation, equal timezone, per-session throttle) make
  // re-renders and post-sync profile updates no-ops.
  useEffect(() => {
    if (isAuthenticated && profile) {
      void syncUserTimezone(profile);
    }
  }, [isAuthenticated, profile]);

  // --- Render Logic ---

  // 1. Show loader if initial auth check is running. A public page keeps
  // rendering instead: it hosts its own sign-in (the webinar live page's
  // JoinCard modal), and swapping it for the spinner while `login()` is in
  // flight unmounts that page — a failed attempt then remounts it with the
  // modal closed and the error gone, so the student just sees "Join in"
  // again (ORA-814).
  if (isPotentiallyAuthenticating && !isAuthenticated && !isPublicPath) {
    return <LoadingSpinner />;
  }

  // 2. Handle redirection if not authenticated
  if (!isAuthenticated) {
    if (isPublicPath) {
      // Render the child component (e.g., Demo), which should handle the !isAuthenticated state itself
      return renderChildren();
    } else {
      return (
        <Navigate
          to={`${authPath}?redirect=${encodeURIComponent(
            `${location.pathname}${location.search}${location.hash}`
          )}`}
          state={{ from: location }}
          replace
        />
      );
    }
  }

  // 3. Authenticated: wait for the identity check (a revoked cookie signs
  // out, another account resets the state), then for the essential data.
  if (!isIdentityChecked && !keepsPublicChildren) {
    return <LoadingSpinner />;
  }

  // Show loader while fetching essential data
  // Profile and the initial exam metadata are required before rendering onboarding/protected children.
  // Later exam refreshes should not unmount the route.
  if (
    (!profile || (isInitialExamMetadataLoading && !isSecuritySettings)) &&
    !keepsPublicChildren
  ) {
    // If data fetch failed, the useEffect should trigger logout, leading back to step 2.
    // So, we only show loading spinner here if loading or profile hasn't populated yet.
    return <LoadingSpinner />;
  }

  // 4. If authenticated and profile loaded but user not onboarded yet,
  // redirect to onboarding before rendering protected children to avoid flash
  if (
    profile &&
    profile.isOnboarded === false &&
    !isAllowedPreOnboardingPath(location.pathname) &&
    !isSecuritySettings
  ) {
    // The query survives: a checkout return (`?checkout-success=true`) can
    // land here before onboarding is done, and the session gates read it.
    return (
      <Navigate
        to={{ pathname: '/onboarding', search: location.search }}
        replace
      />
    );
  }

  // 5. Authenticated and essential data loaded (or a public page already
  // on screen, see above). Render the protected content
  return renderChildren();
};

// Simple loading spinner component (keep as is or use your app's standard loader)
const LoadingSpinner = () => (
  <div
    style={{
      height: '100vh', // Make it full viewport height for clarity
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner />
  </div>
);

export default ProtectedRoute;
